import { Dayjs } from '@dw/dayjs';

/**
 * The seven backgrounds the platform ships with.
 *
 * **No longer the whole story.** A school's palette is now its own data — it can recolour these
 * seven, add to them and delete them (see `ISchoolNoticeTheme`). This enum survives because the API
 * still reports it on every notice for app builds that predate custom themes, and because each
 * value's lowercased name is the well-known id of the theme it seeded. Mirrors the API's
 * `NoticeTheme` enum by name — the API serializes enums as their PascalCase name.
 *
 * Read `INotice.themeStyle` to draw a notice. Read this only to talk to something older.
 */
export enum NOTICE_THEME {
  PLAIN = 'Plain',
  BUTTER = 'Butter',
  ROSE = 'Rose',
  MINT = 'Mint',
  SKY = 'Sky',
  LILAC = 'Lilac',
  INK = 'Ink'
}

/** What each tint looks like, so the swatches show a studio the real thing rather than a name. */
export const NOTICE_THEME_SWATCHES: Record<NOTICE_THEME, string> = {
  [NOTICE_THEME.PLAIN]: '#FFFCF8',
  [NOTICE_THEME.BUTTER]: '#FBEFC9',
  [NOTICE_THEME.ROSE]: '#F9E2E2',
  [NOTICE_THEME.MINT]: '#DDEFE4',
  [NOTICE_THEME.SKY]: '#DCE9F5',
  [NOTICE_THEME.LILAC]: '#E7E0F4',
  [NOTICE_THEME.INK]: '#10403E'
};

export const NOTICE_THEME_LABELS: Record<NOTICE_THEME, string> = {
  [NOTICE_THEME.PLAIN]: 'Plain',
  [NOTICE_THEME.BUTTER]: 'Butter',
  [NOTICE_THEME.ROSE]: 'Rose',
  [NOTICE_THEME.MINT]: 'Mint',
  [NOTICE_THEME.SKY]: 'Sky',
  [NOTICE_THEME.LILAC]: 'Lilac',
  [NOTICE_THEME.INK]: 'Ink'
};

/** The one dark tint. Its swatch needs light text, as the notice itself does in the app. */
export const DARK_NOTICE_THEMES: NOTICE_THEME[] = [NOTICE_THEME.INK];

export const isDarkNoticeTheme = (theme?: NOTICE_THEME): boolean =>
  !!theme && DARK_NOTICE_THEMES.includes(theme);

/**
 * How a notice is actually drawn — resolved by the API and carried on the notice itself.
 *
 * Server-resolved rather than joined here on purpose: the portal's preview and the dancer app's
 * board then cannot disagree about what a notice looks like, and a notice whose theme was deleted
 * still arrives with real colours rather than a dangling id.
 */
export interface INoticeThemeStyle {
  backgroundColor: string;
  borderColor: string;
  /** In px. Zero — no border at all — is the ordinary case and what all seven built-ins carry. */
  borderWidth: number;
  textColor: string;
  /**
   * Whether this paper needs light chrome on it. Decided by the API so the apps — which ship through
   * store review — need no copy of the threshold. The portal's own editor cannot use it: it previews
   * colours that have not been saved yet, so it measures them locally with `isDarkBackgroundColor`.
   */
  isDarkBackground: boolean;
}

/** One theme in a school's palette, as the portal manages it. */
export interface ISchoolNoticeTheme extends INoticeThemeStyle {
  noticeThemeId: string;
  name: string;
  /** Ascending, sparse — sort by it, never derive an index from it. */
  displayOrder: number;
  /**
   * Whether this is one of the seven the platform ships. A label only: a built-in is edited and
   * deleted exactly like any other theme.
   */
  isBuiltIn: boolean;
  auditUserId?: string;
  createdTimestamp?: Dayjs;
  updatedTimestamp?: Dayjs;
}

/** Create and update send the same fields; the id travels in the URL. */
export interface ISaveNoticeThemeRequest {
  name: string;
  backgroundColor: string;
  borderColor: string;
  borderWidth: number;
  textColor: string;
}

export interface IUpdateNoticeThemeRequest extends ISaveNoticeThemeRequest {
  noticeThemeId: string;
}

/** These three mirror the API's notice theme rules exactly — change them there first. */
export const NOTICE_THEME_NAME_MAX_LENGTH = 30;
export const NOTICE_THEME_MAX_BORDER_WIDTH = 8;
export const NOTICE_THEME_MAX_PER_SCHOOL = 30;

/**
 * A theme's name, as something React can definitely render.
 *
 * `cleanJsonResponse` walks every response value and revives anything matching `YYYY-MM-DD` into a
 * `Dayjs`. A theme name is studio free text, so "2026-12-05" — a recital date, which is not a
 * far-fetched name for a theme — arrives as an OBJECT still typed `string`, and rendering it throws
 * "Objects are not valid as a React child". Every render of a theme name goes through here.
 */
export const noticeThemeName = (theme: Pick<ISchoolNoticeTheme, 'name'>): string =>
  typeof theme.name === 'string' ? theme.name : String(theme.name ?? '');

/**
 * The border half of a theme, as `sx`.
 *
 * One definition because the rule is load bearing: **at zero width nothing is emitted at all**, so a
 * school that has never touched its palette renders exactly what it did before themes existed. It
 * also always sets `borderStyle`, which the four hand-written copies this replaces did not — two of
 * them set only width and colour and relied on their own stylesheet having declared a `solid`
 * border first, so moving one of those components would have silently stopped its border painting.
 */
export const noticeThemeBorderSx = (style: Pick<INoticeThemeStyle, 'borderWidth' | 'borderColor'>) =>
  style.borderWidth > 0
    ? { borderWidth: `${style.borderWidth}px`, borderStyle: 'solid', borderColor: style.borderColor }
    : {};

/**
 * The colours to draw a notice in.
 *
 * Prefers what the API resolved. The fallback exists for one narrow case — a portal build talking
 * to an API rolled back to before themes — and keeps a board rendering in its old tint rather than
 * throwing on a missing object.
 */
export const resolveNoticeThemeStyle = (notice: Pick<INotice, 'theme' | 'themeStyle'>): INoticeThemeStyle =>
  notice.themeStyle ?? {
    backgroundColor: NOTICE_THEME_SWATCHES[notice.theme] ?? NOTICE_THEME_SWATCHES[NOTICE_THEME.PLAIN],
    borderColor: NOTICE_THEME_SWATCHES[notice.theme] ?? NOTICE_THEME_SWATCHES[NOTICE_THEME.PLAIN],
    borderWidth: 0,
    textColor: isDarkNoticeTheme(notice.theme) ? '#FBF6F1' : '#2A2521',
    isDarkBackground: isDarkNoticeTheme(notice.theme)
  };

export interface INoticeAttachment {
  key: string;
  fileName: string;
  contentType: string;
  fileSizeInBytes: number;
}

/**
 * One link pinned alongside a notice.
 *
 * The description is what a person actually taps, so it is required rather than a nicety: a link
 * without one is a bare URL on a poster, which tells a family nothing about where it goes until
 * after they have followed it.
 */
export interface INoticeLink {
  description: string;
  url: string;
}

export interface INotice {
  noticeId: string;
  danceSchoolId: string;
  /**
   * The studio's own heading. Optional permanently — blank is ordinary, and every notice written
   * before titles existed has none. Never substitute the body's first line for a missing one.
   */
  title?: string;
  text: string;
  /**
   * The nearest built-in tint, computed by the API for app builds that predate custom themes. Not
   * what the portal draws — use `themeStyle`.
   */
  theme: NOTICE_THEME;
  /**
   * The school theme this notice renders as. Always resolves to a theme the school currently has:
   * if the notice's own theme was deleted, this is the one it fell back to.
   *
   * Sent straight back on save, which is what makes editing a notice whose theme has since been
   * deleted succeed rather than 400.
   */
  themeId: string;
  /** Absent only from an API older than themes — read it through `resolveNoticeThemeStyle`. */
  themeStyle?: INoticeThemeStyle;
  /** Null means "already showing". */
  showAt?: Dayjs;
  /** Null means "never expires". */
  removeAt?: Dayjs;
  attachments: INoticeAttachment[];
  /** Empty — never absent — on every notice written before links existed. */
  links: INoticeLink[];
  visibleToTeachers: boolean;
  /**
   * The studio's chosen position, ascending. May be negative and is not contiguous — sort by it,
   * never derive an index from it.
   */
  displayOrder: number;
  /**
   * Whether the apps are showing this right now, decided server side.
   *
   * Read this rather than re-deriving it from `showAt`/`removeAt`: the server judges every notice
   * in a response against one instant, so a locally-computed badge would drift from what families
   * actually see the moment a clock or timezone differed.
   */
  isLive: boolean;
  auditUserId: string;
  auditUserName: string;
  createdTimestamp: Dayjs;
  updatedTimestamp?: Dayjs;
}

/** Where a new notice lands. A placement choice, used once and never stored — so it is create-only. */
export interface ICreateNoticeRequest {
  title?: string;
  text: string;
  /** The school theme to pin it on. */
  noticeThemeId: string;
  showAt?: string;
  removeAt?: string;
  attachmentKeys: string[];
  links: INoticeLink[];
  visibleToTeachers: boolean;
  addToTop: boolean;
}

/** No `addToTop`: by edit time the notice has a position, and dragging is how that changes. */
export interface IUpdateNoticeRequest {
  noticeId: string;
  title?: string;
  text: string;
  noticeThemeId: string;
  showAt?: string;
  removeAt?: string;
  attachmentKeys: string[];
  links: INoticeLink[];
  visibleToTeachers: boolean;
}

export interface IReorderNoticesRequest {
  /** The COMPLETE board, in the order it should render. */
  noticeIds: string[];
}

export const NOTICE_TITLE_MAX_LENGTH = 60;
export const NOTICE_TEXT_MAX_LENGTH = 2000;
export const NOTICE_MAX_ATTACHMENTS = 5;
export const NOTICE_MAX_ATTACHMENT_SIZE_MB = 5;

/** These three mirror the API's notice validator exactly — change them there first. */
export const NOTICE_MAX_LINKS = 5;
export const NOTICE_LINK_DESCRIPTION_MAX_LENGTH = 80;
export const NOTICE_LINK_URL_MAX_LENGTH = 500;

/** How a notice's schedule reads at a glance. Live is the only one families ever see. */
export enum NOTICE_STATUS {
  LIVE = 'Live',
  SCHEDULED = 'Scheduled',
  EXPIRED = 'Expired'
}

/**
 * Which badge a notice wears.
 *
 * `isLive` is the server's answer and is trusted as-is; `showAt` is consulted only to tell the two
 * NOT-live cases apart, which is a question about intent ("not yet" vs "no longer") rather than a
 * second opinion on whether it is showing.
 */
export const getNoticeStatus = (notice: INotice): NOTICE_STATUS => {
  if (notice.isLive) {
    return NOTICE_STATUS.LIVE;
  }

  return notice.showAt && notice.showAt.isAfter(new Date())
    ? NOTICE_STATUS.SCHEDULED
    : NOTICE_STATUS.EXPIRED;
};
